import React, {FC, ReactNode, useRef} from 'react';
import {useTranslation} from 'react-i18next';

import heroCenter from 'assets/v3/hero/hero-center.png';

import {OuterOrbitLabel, outerOrbitDelay, outerOrbitLabels} from '../../data';
import useOrbitSpeedControl from '../../hooks/useOrbitSpeedControl';

/**
 * Hero 右侧轨道舞台：中心插画（全球社交电商 3D 插画，方形大图）+ 外环 12 平台标签
 * （52s 顺时针）。语言标签内环已按业务要求移除（2026-09）。DOM 结构与动画相位照抄
 * 设计稿，相位公式见 data.tsx。悬停变速（useOrbitSpeedControl）挂在本组件根节点。
 * 中心为方形大图（w-[27rem] 无投影圆角卡片）。
 * 轨道标签数据（平台名）为静态数据，仅「更多」一条 UI 文案在渲染处按语言覆盖（i18n）。
 */

const OrbitStage: FC = () => {
  const stageRef = useRef<HTMLDivElement>(null);
  const {t} = useTranslation('home');
  useOrbitSpeedControl(stageRef);

  /** 外环单个标签 */
  const renderOuterLabel = (label: OuterOrbitLabel): ReactNode => (
    <div className="absolute inset-0" key={label.title} style={{transform: `rotate(${label.angleDeg}deg)`}}>
      <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2">
        <div className="orbit-label-outer" style={{'--a': `${label.angleDeg}deg`} as React.CSSProperties}>
          <span
            className="orbit-depth chip-solid h-11 px-2.5 rounded-xl shadow-md flex items-center justify-center gap-1.5"
            style={{animationDelay: outerOrbitDelay(label.angleDeg)}}
            title={label.dotChip ? t('orbit.morePlatforms') : label.title}
          >
            {label.icon && (
              <img alt="" aria-hidden="true" className={label.iconClass} decoding="async" src={label.icon} />
            )}
            {label.dotChip && <span className="w-2 h-2 rounded-full bg-gradient-to-r from-indigo-500 to-purple-500" />}
            {label.showName && <span className="text-xs font-medium text-slate-700">{label.showName}</span>}
            {label.dotChip && <span className="text-xs font-medium">{t('orbit.more')}</span>}
          </span>
        </div>
      </div>
    </div>
  );

  return (
    <div className="relative hidden lg:block">
      <div className="orbit-stage relative w-full aspect-square max-w-lg mx-auto" ref={stageRef}>
        {/* 中心插画：方形大图（白底 3D 插画，无投影，业务要求 2026-09）。w-[27rem](432px) 为设计定值 */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[27rem] z-20">
          <img alt={t('orbit.centerAlt')} className="w-full h-auto rounded-3xl" decoding="async" src={heroCenter} />
        </div>

        {/* 外环：跨境电商/广告平台，12 个，每 30°，52s 顺时针，倾斜椭圆轨迹，
            圆心与中心插画重合（不偏移） */}
        <div className="absolute -inset-[20%] z-30">
          <div className="absolute inset-0 orbit-ring">{outerOrbitLabels.map(renderOuterLabel)}</div>
        </div>
      </div>
    </div>
  );
};

export default OrbitStage;
